import { twMerge } from "tailwind-merge";
import CryptoJS from "crypto-js";

export function cn(...inputs) {
  return twMerge(inputs);
}

export function encryptData(data, secretKey) {
  const encryptedData = CryptoJS.AES.encrypt(
    JSON.stringify(data),
    secretKey
  ).toString();
  return encryptedData;
}

export function decryptData(data, secretKey) {
  const decryptedData = CryptoJS.AES.decrypt(data, secretKey).toString(
    CryptoJS.enc.Utf8
  );
  return JSON.parse(decryptedData);
}

export const getCurrentAndEndDate = () => {
  const today = new Date();
  const endDate = new Date();

  // Set the start time to 00:00:00
  today.setHours(0, 0, 0, 0);

  // Set the end date to 3 days later and time to 23:59:59
  today.setDate(today.getDate() - 5);
  endDate.setHours(23, 59, 59, 999);

  return [today, endDate];
};

export const handlePrepareGraph = (data, timeRange) => {
  // Helper function to get date ranges
  const getDateRange = (timeRange) => {
    const now = new Date();
    const start = new Date(now);
    let end = new Date(now);

    switch (timeRange) {
      case "day":
        start.setDate(now.getDate() - 1);
        break;
      case "week":
        start.setDate(now.getDate() - 7);
        break;
      case "month":
        start.setMonth(now.getMonth() - 1);
        break;
      case "year":
        start.setFullYear(now.getFullYear() - 1);
        break;
      default:
        throw new Error("Invalid time range");
    }
    return { start, end };
  };

  // Step 1: Filter data by the specified time range
  const { start, end } = getDateRange(timeRange);
  const filteredData = data.filter((entry) => {
    const entryDate = new Date(entry.createdAt);
    return entryDate >= start && entryDate <= end;
  });

  // Define the grouping logic based on the time range
  const getGroupIndex = (entryDate, timeRange) => {
    switch (timeRange) {
      case "day":
        return entryDate.getHours();
      case "week":
        return entryDate.getDay();
      case "month":
        return entryDate.getDate();
      case "year":
        return entryDate.getMonth();
      default:
        return 0;
    }
  };

  const timeUnits = {
    day: 24,
    week: 7,
    month: new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate(),
    year: 12,
  };

  const timeLabels = {
    day: Array.from({ length: 24 }, (_, i) => `${i}:00`),
    week: [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ],
    month: Array.from({ length: timeUnits["month"] }, (_, i) => i + 1),
    year: [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
  };

  const initialCumulativeData = {
    cumulativeSleepHours: 0,
    cumulativeMoodLevel: 0,
    cumulativeStressLevel: 0,
    cumulativeAppetiteLevel: 0,
  };

  const chartData = Array.from({ length: timeUnits[timeRange] }, (_, index) => {
    const entriesForGroup = filteredData.filter((entry) => {
      const entryDate = new Date(entry.createdAt);
      return getGroupIndex(entryDate, timeRange) === index;
    });

    const groupSleepHours = entriesForGroup.reduce(
      (total, entry) => total + entry.sleepHours,
      0
    );
    const groupMoodLevel = entriesForGroup.reduce(
      (total, entry) => total + entry.moodLevel,
      0
    );
    const groupStressLevel = entriesForGroup.reduce(
      (total, entry) => total + entry.stressLevel,
      0
    );
    const groupAppetiteLevel = entriesForGroup.reduce(
      (total, entry) => total + entry.appetiteLevel,
      0
    );

    // initialCumulativeData.cumulativeSleepHours += groupSleepHours;
    // initialCumulativeData.cumulativeMoodLevel += groupMoodLevel;
    // initialCumulativeData.cumulativeStressLevel += groupStressLevel;
    // initialCumulativeData.cumulativeAppetiteLevel += groupAppetiteLevel;

    initialCumulativeData.cumulativeSleepHours = groupSleepHours;
    initialCumulativeData.cumulativeMoodLevel = groupMoodLevel;
    initialCumulativeData.cumulativeStressLevel = groupStressLevel;
    initialCumulativeData.cumulativeAppetiteLevel = groupAppetiteLevel;

    return {
      groupIndex: index,
      ...initialCumulativeData,
    };
  });

  // Prepare chart data structure
  const labels = timeLabels[timeRange];
  const sleepHoursData = chartData.map((entry) => entry.cumulativeSleepHours);
  const moodLevelData = chartData.map((entry) => entry.cumulativeMoodLevel);
  const stressLevelData = chartData.map((entry) => entry.cumulativeStressLevel);
  const appetiteLevelData = chartData.map(
    (entry) => entry.cumulativeAppetiteLevel
  );

  const lineChartData = {
    // labels: ["Time", ...labels],
    sleep: [0, ...sleepHoursData],
    mood: [0, ...moodLevelData],
    stress: [0, ...stressLevelData],
    appetite: [0, ...appetiteLevelData],
    // datasets: [
    //   {
    //     label: "Sleep Hours",
    //     data: [0, ...sleepHoursData],
    //     backgroundColor: "#74b9ff",
    //     borderColor: "#74b9ff",
    //     tension: 0.4,
    //     pointRadius: 0,
    //     hoverPointRadius: 0,
    //   },
    //   {
    //     label: "Mood Level",
    //     data: [0, ...moodLevelData],
    //     backgroundColor: "#55efc4",
    //     borderColor: "#55efc4",
    //     tension: 0.4,
    //     pointRadius: 0,
    //     hoverPointRadius: 0,
    //   },
    //   {
    //     label: "Stress Level",
    //     data: [0, ...stressLevelData],
    //     backgroundColor: "#ff7675",
    //     borderColor: "#ff7675",
    //     tension: 0.4,
    //     pointRadius: 0,
    //     hoverPointRadius: 0,
    //   },
    //   {
    //     label: "Appetite Level",
    //     data: [0, ...appetiteLevelData],
    //     backgroundColor: "#ffeaa7",
    //     borderColor: "#ffeaa7",
    //     tension: 0.4,
    //     pointRadius: 0,
    //     hoverPointRadius: 0,
    //   },
    // ],
  };

  return lineChartData;
};
